/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/realtime/hooks/useEventSource.ts
 * layer: web
 * area: hooks
 * kind: hook
 * web_root: hooks
 * relpath: web/src/platform/realtime/hooks/useEventSource.ts
 * filename: useEventSource.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '@/platform/authentication/hooks/useAuth';

/**
 * Server event received from SSE
 */
export interface ServerEvent {
  id: string;
  type: string;
  data: unknown;
  timestamp: string;
  tenantId?: string;
  resourceId?: string;
  resourceType?: string;
}

/**
 * Options for configuring the EventSource hook
 */
export interface EventSourceOptions {
  /** Event types to subscribe to (comma-separated on server) */
  eventTypes?: string[];
  /** Resources to filter events by */
  resources?: string[];
  /** Pattern filters for event types (e.g., "device.*") */
  patterns?: string[];
  /** Callback when an event is received */
  onEvent?: (event: ServerEvent) => void;
  /** Callback when connection is established */
  onConnect?: () => void;
  /** Callback when connection is lost */
  onDisconnect?: () => void;
  /** Callback on error */
  onError?: (error: Error) => void;
  /** Auto-connect on mount (default: true) */
  autoConnect?: boolean;
  /** Enable logging (default: false) */
  debug?: boolean;
}

/** Connection state */
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

/** Return value of useEventSource */
export interface UseEventSourceReturn {
  /** Current connection state */
  connectionState: ConnectionState;
  /** ID of the last received event */
  lastEventId: string | null;
  /** Reconnect to the server */
  reconnect: () => void;
  /** Disconnect from the server */
  disconnect: () => void;
  /** Whether currently connected */
  isConnected: boolean;
  /** Number of reconnection attempts */
  reconnectAttempts: number;
}

const MAX_RECONNECT_ATTEMPTS = 10;
const BASE_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds

/**
 * Hook for connecting to Server-Sent Events (SSE) endpoint
 * Provides automatic reconnection with exponential backoff
 */
export function useEventSource(options: EventSourceOptions = {}): UseEventSourceReturn {
  const { isAuthenticated } = useAuth();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastEventId, setLastEventId] = useState<string | null>(null);
  const [reconnectAttemptsState, setReconnectAttemptsState] = useState(0);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const optionsRef = useRef(options);

  // Keep options ref updated
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const log = useCallback((message: string, ...args: unknown[]) => {
    if (optionsRef.current.debug) {
      console.warn(`[SSE] ${message}`, ...args);
    }
  }, []);

  /**
   * Calculate reconnect delay with exponential backoff and jitter
   */
  const getReconnectDelay = useCallback(() => {
    const attempts = reconnectAttempts.current;
    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(2, attempts),
      MAX_RECONNECT_DELAY
    );
    // Add jitter (±20%)
    const jitter = delay * 0.2 * (Math.random() - 0.5) * 2;
    return Math.floor(delay + jitter);
  }, []);

  /**
   * Build the SSE endpoint URL with filters
   */
  const buildUrl = useCallback(() => {
    const params = new URLSearchParams();
    const opts = optionsRef.current;

    if (opts.eventTypes?.length) {
      params.set('types', opts.eventTypes.join(','));
    }
    if (opts.resources?.length) {
      params.set('resources', opts.resources.join(','));
    }
    if (opts.patterns?.length) {
      params.set('patterns', opts.patterns.join(','));
    }
    if (lastEventId) {
      params.set('lastEventId', lastEventId);
    }

    // Auth is via HttpOnly cookies (sent automatically with withCredentials: true)

    const baseUrl = `${import.meta.env.VITE_API_URL || '/api/v1'}/realtime/events`;
    return params.toString() ? `${baseUrl}?${params}` : baseUrl;
  }, [lastEventId]);

  /**
   * Connect to SSE endpoint
   */
  const connect = useCallback(() => {
    if (!isAuthenticated) {
      log('Cannot connect: not authenticated');
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setConnectionState('connecting');
    log('Connecting to SSE endpoint...');

    const url = buildUrl();

    // Note: Standard EventSource doesn't support custom headers
    // Token should be included via cookie or query param in production
    const eventSource = new EventSource(url, {
      withCredentials: true,
    });

    eventSource.onopen = () => {
      setConnectionState('connected');
      reconnectAttempts.current = 0;
      setReconnectAttemptsState(0);
      log('Connected successfully');
      optionsRef.current.onConnect?.();
    };

    eventSource.onerror = (error) => {
      log('Connection error:', error);

      if (eventSource.readyState === EventSource.CLOSED) {
        setConnectionState('disconnected');
        optionsRef.current.onDisconnect?.();

        // Attempt reconnection
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current++;
          setReconnectAttemptsState(reconnectAttempts.current);
          const delay = getReconnectDelay();

          log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          setConnectionState('error');
          optionsRef.current.onError?.(new Error('Max reconnection attempts reached'));
          log('Max reconnection attempts reached');
        }
      }
    };

    // Handle connection established event
    eventSource.addEventListener('connected', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        log('Connected with client ID:', data.client_id);
      } catch (err) {
        log('Failed to parse connected event:', err);
      }
    });

    // Generic message handler for all events
    eventSource.onmessage = (e: MessageEvent) => {
      try {
        const event: ServerEvent = {
          id: e.lastEventId || '',
          type: 'message',
          data: JSON.parse(e.data),
          timestamp: new Date().toISOString(),
        };
        setLastEventId(e.lastEventId || null);
        optionsRef.current.onEvent?.(event);
      } catch (err) {
        log('Failed to parse message event:', err);
      }
    };

    // Register specific event type listeners
    const eventTypes = optionsRef.current.eventTypes || [
      'device.status',
      'device.connected',
      'device.disconnected',
      'ticket.created',
      'ticket.updated',
      'ticket.assigned',
      'alert.triggered',
      'alert.resolved',
      'notification',
    ];

    eventTypes.forEach((eventType) => {
      eventSource.addEventListener(eventType, (e: MessageEvent) => {
        try {
          const event: ServerEvent = {
            id: e.lastEventId || '',
            type: eventType,
            data: JSON.parse(e.data),
            timestamp: new Date().toISOString(),
          };
          setLastEventId(e.lastEventId || null);
          optionsRef.current.onEvent?.(event);
          log(`Received ${eventType} event:`, event.id);
        } catch (err) {
          log(`Failed to parse ${eventType} event:`, err);
        }
      });
    });

    eventSourceRef.current = eventSource;
  }, [isAuthenticated, buildUrl, getReconnectDelay, log]);

  /**
   * Disconnect from SSE endpoint
   */
  const disconnect = useCallback(() => {
    log('Disconnecting...');

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close EventSource
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setConnectionState('disconnected');
    reconnectAttempts.current = 0;
    setReconnectAttemptsState(0);
  }, [log]);

  // Connect on mount if autoConnect is true (default)
  useEffect(() => {
    if (options.autoConnect !== false && isAuthenticated) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated]);  

  return {
    connectionState,
    lastEventId,
    reconnect: connect,
    disconnect,
    isConnected: connectionState === 'connected',
    reconnectAttempts: reconnectAttemptsState,
  };
}
